import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MapInteractionService } from "./map-interaction.service";
import { FileUploadService } from "./file-upload.service";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { FileReaderService } from "../../../viewer-components/services/readers/file-reader.service";
import { DataDetailDialogComponent } from "../components/content/shared/data-detail-dialog/data-detail-dialog.component";
import { UrlSnippetDialogComponent } from "../components/content/shared/url-snippet-dialog/url-snippet-dialog.component";
import type { LayerDescriptor } from "../../../shared-components/feature-table/feature-table.types";
import { LayerFeaturePanelService } from "../../../shared-components/feature-table/layer-feature-panel.service";
import type { ActionId } from "../model/action.types";
import type { ActionContext } from "../model/data.types";
import { EvidenceLayerService } from "./evidence-layer.service";
import { ViewerHandoffService } from "./viewer-handoff.service";
import { resolveLayerContract } from "../model/evidence-layer.types";
import { MapLayerBlockService } from "./map-layer-block.service";
import { resolveMapLayerContract } from "../model/map-layer.types";
import type { ContentBlockRef } from "../model/block.types";
import type { FeatureCollection } from "geojson";

/** Centralized action executor for all content block actions. */
@Injectable({ providedIn: "root" })
export class ActionService {
  readonly #map = inject(MapInteractionService);

  readonly #dialog = inject(MatDialog);
  readonly #layerFeaturePanel = inject(LayerFeaturePanelService);

  readonly #fileUpload = inject(FileUploadService);

  readonly #fileReader = inject(FileReaderService);

  readonly #evidence = inject(EvidenceLayerService);

  readonly #handoff = inject(ViewerHandoffService);

  /**
   * map_layer door (map-layer-block-ui S11). Optional because this service is ALSO provided at
   * root for contexts outside the chat container (TestBeds mounting the cards without the
   * chat-container providers): a required inject would crash every such construction. In the
   * real app ActionService is re-scoped in chat-container next to MapLayerBlockService, so the
   * map_layer routing always resolves there; absence (never the real app) degrades to a logged
   * no-op — a map_layer block must never fall through to the legacy plain add (its inline
   * style envelope would be silently dropped).
   */
  readonly #mapLayer = inject(MapLayerBlockService, { optional: true });

  /**
   * Executes the given action using the provided context.
   */
  execute(action: ActionId, context: ActionContext): void {
    switch (action) {
      case "zoom_to":
        this.#zoomTo(context);
        break;

      case "move_to":
        this.#moveTo(context);
        break;

      case "view_data":
        if (context.dataResult) {
          this.#dialog.open(DataDetailDialogComponent, {
            data: { data: context.dataResult },
            width: "90vw",
            maxWidth: "1000px",
            minHeight: "350px",
            maxHeight: "90vh",
          });
        }
        break;

      case "download":
        this.#download(context);
        break;

      case "copy":
        this.#copy(context);
        break;

      case "open_url":
        if (context.url) {
          window.open(context.url, "_blank", "noopener,noreferrer");
        }
        break;

      case "show_url_snippet":
        if (context.endpointData) {
          this.#dialog.open(UrlSnippetDialogComponent, {
            data: { endpointData: context.endpointData },
            width: "700px",
            maxWidth: "95vw",
          });
        }
        break;

      case "add_layer":
        void this.#addLayer(context);
        break;

      case "view_table":
        this.#viewTable(context);
        break;

      // Viewer-handoff (viewer-handoff plan F1.6): agent result blocks push into the
      // matching viewer component; both need the owning block's file ref.
      case "open_in_embeddings":
        if (context.block) void this.#handoff.toEmbeddings(context.block);
        else console.warn("ActionService: open_in_embeddings called without a block", context);
        break;

      case "open_in_elements":
        if (context.block) void this.#handoff.toElements(context.block);
        else console.warn("ActionService: open_in_elements called without a block", context);
        break;

      default:
        console.error("ActionService: unknown action:", action, context);
    }
  }

  /**
   * Returns true if the given GeoJSON has at least one feature with extractable coordinates.
   * Use this to decide whether to show geometry-dependent actions (e.g. zoom_to).
   */
  hasExtractableGeometry(geojson: unknown): boolean {
    if (!geojson) return false;
    const filtered = this.#withValidGeometry(geojson) as Record<string, unknown>;
    // FeatureCollection with no features that have geometry → no coordinates to zoom to
    if (filtered?.type === "FeatureCollection") {
      const features = filtered.features as unknown[];
      if (!features?.length) return false;
    }
    try {
      const [minX] = GeoService.bbox(filtered as any);
      return isFinite(minX);
    } catch {
      return false;
    }
  }

  #zoomTo(context: ActionContext): void {
    if (context.bounds) {
      this.#map.zoomToExtent(...context.bounds);
      return;
    }
    if (context.geometry) {
      try {
        const [minX, minY, maxX, maxY] = GeoService.bbox(this.#withValidGeometry(context.geometry) as any);
        if (isFinite(minX)) {
          this.#map.zoomToExtent(minX, minY, maxX, maxY);
          return;
        }
      } catch {
        // fall through to warn
      }
    }
    console.warn("ActionService: zoom_to called without bounds or geometry", context);
  }

  /** Strips features with null/undefined geometry so turf never throws on them. */
  #withValidGeometry(geojson: unknown): unknown {
    if (!geojson || typeof geojson !== "object") return geojson;
    const g = geojson as Record<string, unknown>;
    if (g.type === "FeatureCollection" && Array.isArray(g.features)) {
      return { ...g, features: (g.features as any[]).filter((f) => f?.geometry != null) };
    }
    return geojson;
  }

  #moveTo(context: ActionContext): void {
    if (context.point) {
      this.#map.moveTo(context.point.lng, context.point.lat, context.point.zoom);
      return;
    }
    console.warn("ActionService: move_to called without point", context);
  }

  #download(context: ActionContext): void {
    const url = context.url ?? context.dataResult?.url;
    const filename = context.filename ?? context.dataResult?.filename;

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const dr = context.dataResult;

    // GeoTIFF arrived via SSE as base64 — reconstruct data URL for download
    if (dr?.geotiff) {
      const a = document.createElement("a");
      a.href = `data:image/tiff;base64,${dr.geotiff}`;
      a.download = filename ?? "data.tif";
      a.click();
      return;
    }

    let content: string | undefined;
    let mimeType = "text/plain";
    let defaultName = "download";

    if (dr?.json !== undefined) {
      content = JSON.stringify(dr.json, null, 2);
      mimeType = "application/json";
      defaultName = "data.json";
    } else if (dr?.geojson !== undefined) {
      content = JSON.stringify(dr.geojson, null, 2);
      mimeType = "application/geo+json";
      defaultName = "data.geojson";
    } else if (dr?.text) {
      content = dr.text;
      mimeType = "text/plain";
      defaultName = "data.txt";
    }

    if (!content) {
      console.warn("ActionService: download called without url or downloadable data", context);
      return;
    }

    const blob = new Blob([content], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename ?? defaultName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  }

  async #copy(context: ActionContext): Promise<void> {
    const text =
      context.textContent ??
      context.dataResult?.text ??
      (context.dataResult?.json !== undefined ? JSON.stringify(context.dataResult.json, null, 2) : undefined) ??
      (context.dataResult?.geojson !== undefined ? JSON.stringify(context.dataResult.geojson, null, 2) : undefined) ??
      // Image/GeoTIFF results carry no inline payload — copy the resource URL instead,
      // with the same precedence #download uses (explicit context.url first).
      (context.url ?? context.dataResult?.url);

    if (!text) {
      console.warn("ActionService: copy called without text content", context);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("ActionService: clipboard write failed:", err);
    }
  }

  async #addLayer(context: ActionContext): Promise<void> {
    // map_layer blocks (map-layer-block-ui S11) route FIRST: MapLayerBlockService owns the
    // S2/S3 update-vs-add decision, the fetch, the inline style bridge, the S10 toasts and
    // the legend. Blocks without the `map_layer` contract keep the existing routing below,
    // byte-identical.
    const mapLayerBlock = context.block;
    if (mapLayerBlock && resolveMapLayerContract(mapLayerBlock)) {
      if (!this.#mapLayer) {
        console.warn(
          "ActionService: map_layer block without MapLayerBlockService in scope (outside the chat container) — add_layer ignored",
        );
        return;
      }
      await this.#mapLayer.applyFromBlock(mapLayerBlock);
      return;
    }

    if (context.dataResult?.type === "geotiff") {
      this.#addGeoTiffLayer(context);
      return;
    }

    // Evidence-layer contract path (evidence-layer-ui US3/US4): a block carrying `data.layer`
    // goes through the typed EvidenceLayerService flow — style by contract type, name
    // `layer.label ?? data.label`, plan group via plan_ref. Blocks without the contract keep
    // the legacy path below, byte-identical.
    const block = context.block;
    if (block && resolveLayerContract(block)) {
      await this.#evidence.addToMapFromBlock(block);
      return;
    }

    // Fast path: GeoJSON already in memory (ToolDataCard inline, or FileBlockCard auto-loaded < 500 KB).
    let geojson: unknown = context.geometry ?? context.dataResult?.geojson;

    // File-ref path (primary in production): FileBlockCard always sets ctx.url = authenticatedUrl(block.url).
    // When the file was not auto-loaded (> 500 KB), only the URL is available — fetch it here.
    if (!geojson && context.url) {
      try {
        const ref = { filename: context.filename ?? "layer.geojson", size_bytes: null } as Pick<
          ContentBlockRef,
          "filename" | "size_bytes"
        >;
        const result = await firstValueFrom(
          this.#fileUpload.fetchFileContent(context.url, "application/geo+json", ref),
        );
        geojson = result.geojson;
      } catch (err) {
        console.error("ActionService: add_layer fetch failed", context.url, err);
        return;
      }
    }

    if (!geojson) {
      console.warn("ActionService: add_layer called without geometry or url", context);
      return;
    }

    const layerName = context.filename?.replace(/\.[^.]+$/, "") ?? "AI Layer";
    const normResult = this.#fileReader.prepareGeoJson(geojson, `${layerName}.geojson`);
    if (!normResult.data) {
      console.error("ActionService: add_layer normalization failed", normResult.errors);
      return;
    }
    this.#map.addGeoJsonLayer(normResult.data, undefined, layerName);
  }

  #addGeoTiffLayer(context: ActionContext): void {
    const layerName = context.filename?.replace(/\.[^.]+$/, "") ?? "AI GeoTIFF Layer";
    let url = context.url ?? context.dataResult?.url;

    // Inline base64 (SSE preview path, no backend URL) — decode to a Blob and use an object URL.
    // Note: object URLs don't survive a page reload, unlike the URL-based path used by file attachments.
    if (!url && context.dataResult?.geotiff) {
      const bytes = Uint8Array.from(atob(context.dataResult.geotiff), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "image/tiff" });
      url = URL.createObjectURL(blob);
    }

    if (!url) {
      console.warn("ActionService: add_layer(geotiff) called without url or base64 data", context);
      return;
    }

    this.#map.addGeoTiffLayer(url, layerName);
  }

  #viewTable(context: ActionContext): void {
    const geojson = context.dataResult?.geojson ?? context.geometry;
    if (!geojson) {
      console.warn("ActionService: view_table called without geojson data", context);
      return;
    }
    const collection = geojson as FeatureCollection;
    const features = collection.features ?? [];
    const layerName = context.filename?.replace(/\.[^.]+$/, "") ?? "GeoJSON Layer";
    const descriptor: LayerDescriptor = {
      layerName,
      totalFeatures: features.length,
      loadPage: (page, pageSize) => {
        const start = page * pageSize;
        return Promise.resolve({
          type: "FeatureCollection",
          features: features.slice(start, start + pageSize),
        } as FeatureCollection);
      },
      getAllFeatures: () => features,
      collectionProperties: (collection as any).properties,
    };
    this.#layerFeaturePanel.open({ layers: [descriptor] });
  }

}
